import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AlertCircle, ArrowLeftRight, Ban, Check, CheckCircle2, CreditCard, HelpCircle,
  BookOpen, History, Landmark, Play, RefreshCw, Search, Sparkles, Tag, User, Wand2, X,
} from 'lucide-react';
import api from '../../services/api';

const ENTITY_MAP = { usa: 'Eyvo USA', india: 'Eyvo India' };

// The four outcomes the engine can return. `blocked` is not work — it is a
// transfer or a card payment, already settled, and it is shown so that the
// absence of a category reads as deliberate rather than as something missed.
const OUTCOMES = [
  { key: '',          label: 'Everything' },
  { key: 'review',    label: 'Needs a category', tone: 'amber' },
  { key: 'suggested', label: 'Suggested',        tone: 'indigo' },
  { key: 'auto',      label: 'Categorized',      tone: 'emerald' },
  { key: 'blocked',   label: 'Not an expense',   tone: 'slate' },
];

const KIND_ICON = {
  transfer:            ArrowLeftRight,
  credit_card_payment: CreditCard,
  refund:              RefreshCw,
  income:              Landmark,
};

const money = (n) => {
  if (n === null || n === undefined) return '—';
  const s = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
    .format(Math.abs(n));
  return n < 0 ? s : `+${s}`;
};

// Written out rather than interpolated: Tailwind scans source text for class
// names, so `bg-${tone}-50` is generated by nothing and ships as no styling at
// all. Every class that can appear has to appear literally somewhere.
const PILL_TONE = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  indigo:  'bg-indigo-50 text-indigo-700 border-indigo-200',
  amber:   'bg-amber-50 text-amber-700 border-amber-200',
  slate:   'bg-slate-100 text-slate-600 border-slate-200',
};

const TILE_TONE = {
  emerald: 'text-emerald-700',
  indigo:  'text-indigo-700',
  amber:   'text-amber-700',
  slate:   'text-slate-700',
};

const Pill = ({ tone = 'slate', children, title }) => (
  <span
    title={title}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${PILL_TONE[tone] || PILL_TONE.slate}`}
  >
    {children}
  </span>
);

/**
 * Confidence is shown as the evidence behind it, not as a bare number.
 * "795 of 796" is a claim someone can check against the ledger; "0.99" is not.
 */
const Confidence = ({ decision }) => {
  if (decision.tier === 1) {
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700">
      <User size={11} /> Set by hand
    </span>;
  }
  if (!decision.confidence) return <span className="text-slate-300">—</span>;
  const pct = Math.round(decision.confidence * 100);
  const tone = pct >= 95 ? 'bg-emerald-500' : pct >= 80 ? 'bg-amber-400' : 'bg-slate-300';
  return (
    <span className="inline-flex items-center gap-1.5" title={decision.explanation}>
      <span className="w-10 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <span className={`block h-full ${tone}`} style={{ width: `${pct}%` }} />
      </span>
      <span className="text-[10px] font-medium text-slate-500 tabular-nums">{pct}%</span>
    </span>
  );
};

const SummaryTile = ({ label, value, sub, tone = 'slate', active, onClick }) => (
  <button
    onClick={onClick}
    className={`text-left px-4 py-3 rounded-xl border transition-colors ${
      active ? 'border-indigo-400 bg-indigo-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'
    }`}
  >
    <div className={`text-xl font-bold tabular-nums ${TILE_TONE[tone] || TILE_TONE.slate}`}>{value}</div>
    <div className="text-[11px] font-medium text-slate-600 mt-0.5">{label}</div>
    {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
  </button>
);

function CategoryPicker({ accounts, value, onPick, onCancel, busy }) {
  const [query, setQuery] = useState('');
  const [always, setAlways] = useState(false);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? accounts.filter(a =>
          (a.account_name || '').toLowerCase().includes(q) ||
          (a.account_code || '').toLowerCase().includes(q))
      : accounts;
    return list.slice(0, 40);
  }, [accounts, query]);

  return (
    <div className="border border-indigo-200 bg-indigo-50/40 rounded-lg p-3 mt-2" data-testid="category-picker">
      <div className="flex items-center gap-2 mb-2">
        <Search size={13} className="text-slate-400" />
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Find an account…"
          className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-indigo-400"
        />
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
      </div>

      <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 bg-white rounded border border-slate-200">
        {shown.map(a => (
          <button
            key={a.account_id || a.account_code}
            disabled={busy}
            onClick={() => onPick(a.account_code, always)}
            className={`w-full text-left px-3 py-2 text-xs hover:bg-indigo-50 flex items-center gap-2 ${
              value === a.account_code ? 'bg-indigo-50' : ''
            }`}
          >
            <span className="font-mono text-[10px] text-slate-400 w-14 shrink-0">{a.account_code}</span>
            <span className="text-slate-700">{a.account_name}</span>
          </button>
        ))}
        {shown.length === 0 && (
          <div className="px-3 py-4 text-xs text-slate-400 text-center">No account matches that.</div>
        )}
      </div>

      {/*
        The difference between correcting one transaction and changing how a
        merchant is treated forever. It has to be a deliberate act, so it is
        off by default and says exactly what it will do.
      */}
      <label className="flex items-start gap-2 mt-2.5 cursor-pointer">
        <input type="checkbox" checked={always} onChange={e => setAlways(e.target.checked)}
               className="mt-0.5" data-testid="always-checkbox" />
        <span className="text-[11px] text-slate-600 leading-snug">
          <strong>Always categorize transactions like this the same way.</strong>{' '}
          Creates a rule that outranks anything learned from history. Without it,
          this changes only this transaction — and counts towards a suggestion later.
        </span>
      </label>
    </div>
  );
}

function Row({ txn, accounts, onApprove, onChange, onHistory, busy }) {
  const [picking, setPicking] = useState(false);
  const d = txn.decision;
  const KindIcon = KIND_ICON[d.txn_kind];

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50/60 align-top" data-testid="cat-row">
        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap tabular-nums">{txn.date || '—'}</td>
        <td className="px-3 py-2.5">
          <div className="text-slate-700 flex items-center gap-1.5">
            {txn.bank_account_name || '—'}
          </div>
        </td>
        <td className="px-3 py-2.5">
          <div className="font-medium text-slate-800">{d.merchant || <span className="text-slate-300">unreadable</span>}</div>
          {d.descriptor && <div className="text-[10px] text-slate-400">{d.descriptor}</div>}
        </td>
        <td className="px-3 py-2.5 text-slate-600 max-w-xs">
          <div className="truncate" title={txn.description}>{txn.description}</div>
        </td>
        <td className={`px-3 py-2.5 text-right tabular-nums font-medium whitespace-nowrap ${
          (txn.amount ?? 0) < 0 ? 'text-slate-800' : 'text-emerald-700'}`}>
          {money(txn.amount)}
        </td>
        <td className="px-3 py-2.5">
          {d.outcome === 'blocked' ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
              {KindIcon && <KindIcon size={12} />}
              {d.txn_kind === 'credit_card_payment' ? 'Card payment' : 'Transfer'}
            </span>
          ) : d.account_code ? (
            <div>
              <div className="text-slate-800">{d.account_name || d.account_code}</div>
              <div className="text-[10px] text-slate-400 font-mono">{d.account_code}</div>
            </div>
          ) : (
            <span className="text-amber-600 text-[11px] font-medium">Needs a category</span>
          )}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          {d.outcome === 'blocked'
            ? <Pill tone="slate" title={d.explanation}><Ban size={10} /> Not an expense</Pill>
            : d.tier === 1
              ? <Pill tone="slate" title={d.explanation}><User size={10} /> Manual</Pill>
              : d.outcome === 'auto'
                ? <Pill tone="emerald" title={d.explanation}><Sparkles size={10} /> Automatic</Pill>
                : d.outcome === 'suggested'
                  ? <Pill tone="indigo" title={d.explanation}><Wand2 size={10} /> Suggested</Pill>
                  : <Pill tone="amber" title={d.explanation}><AlertCircle size={10} /> Review</Pill>}
          <div className="text-[9px] text-slate-400 mt-1">{d.tier_label}</div>
        </td>
        <td className="px-3 py-2.5"><Confidence decision={d} /></td>
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          <div className="inline-flex items-center gap-1">
            {d.outcome === 'suggested' && (
              <button
                disabled={busy}
                onClick={() => onApprove(txn.txn_id)}
                data-testid="approve-btn"
                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-700 text-white text-[11px] font-semibold rounded hover:bg-emerald-800 disabled:opacity-50"
              >
                <Check size={11} /> Approve
              </button>
            )}
            {d.outcome !== 'blocked' && (
              <button
                disabled={busy}
                onClick={() => setPicking(p => !p)}
                data-testid="change-btn"
                className="inline-flex items-center gap-1 px-2 py-1 border border-slate-200 text-slate-600 text-[11px] font-medium rounded hover:bg-white disabled:opacity-50"
              >
                <Tag size={11} /> {d.account_code ? 'Change' : 'Categorize'}
              </button>
            )}
            <button
              onClick={() => onHistory(txn.txn_id)}
              title="Why is it in this category, and what was it before?"
              className="p-1 text-slate-400 hover:text-slate-700"
              data-testid="history-btn"
            >
              <History size={13} />
            </button>
          </div>
        </td>
      </tr>
      {picking && (
        <tr className="bg-slate-50/60">
          <td colSpan={9} className="px-3 pb-3">
            <CategoryPicker
              accounts={accounts}
              value={d.account_code}
              busy={busy}
              onCancel={() => setPicking(false)}
              onPick={async (code, always) => { await onChange(txn.txn_id, code, always); setPicking(false); }}
            />
          </td>
        </tr>
      )}
      {/* The explanation is always present, never only in a tooltip: an
          automatic decision nobody can read is not auditable. */}
      <tr className="border-b border-slate-100">
        <td />
        <td colSpan={8} className="px-3 pb-2 -mt-1">
          <div className="text-[10px] text-slate-400 flex items-center gap-1">
            <HelpCircle size={10} className="shrink-0" />
            {d.explanation}
          </div>
        </td>
      </tr>
    </>
  );
}

export default function ExpenseCategorization() {
  const { pathname } = useLocation();
  const entitySlug = pathname.split('/')[2];
  const entity = ENTITY_MAP[entitySlug] || entitySlug;

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [notice, setNotice] = useState('');
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [showRules, setShowRules] = useState(false);
  const [rules, setRules] = useState([]);

  const [outcome, setOutcome] = useState('review');
  const [search, setSearch] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [manualOnly, setManualOnly] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams({ entity });
    if (outcome) p.set('outcome', outcome);
    if (search.trim()) p.set('search', search.trim());
    if (accountCode) p.set('account_code', accountCode);
    if (bankAccountId) p.set('bank_account_id', bankAccountId);
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo) p.set('date_to', dateTo);
    if (manualOnly) p.set('manual_only', 'true');
    return p.toString();
  }, [entity, outcome, search, accountCode, bankAccountId, dateFrom, dateTo, manualOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [txnRes, sumRes] = await Promise.all([
        api.get(`/api/categorization/transactions?${params}`),
        api.get(`/api/categorization/summary?entity=${encodeURIComponent(entity)}`),
      ]);
      setRows(txnRes.data.transactions || []);
      setSummary(sumRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [params, entity]);

  useEffect(() => {
    (async () => {
      try {
        const [accRes, bankRes] = await Promise.all([
          api.get(`/api/accounts/chart?entity=${encodeURIComponent(entity)}`),
          api.get('/api/bank-accounts'),
        ]);
        setAccounts((accRes.data || []).filter(a => a.is_active !== false));
        setBanks(bankRes.data || []);
      } catch (e) { console.error(e); }
    })();
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 7000); };
  const mark = (id, v) => setBusy(p => ({ ...p, [id]: v }));

  const runCategorization = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/categorization/run', { entity, only_undecided: true });
      const r = res.data;
      flash(`Categorized ${r.auto} automatically, proposed ${r.suggested}, left ${r.review} for review, ` +
            `set aside ${r.blocked} as transfers or card payments. ${r.manual_preserved} manual categories untouched.`);
      await load();
    } catch (e) { flash(e.response?.data?.detail || 'Categorization failed.'); }
    setLoading(false);
  };

  const approve = async (txnId) => {
    mark(txnId, true);
    try {
      await api.post(`/api/categorization/transactions/${txnId}/approve`);
      await load();
    } catch (e) { flash(e.response?.data?.detail || 'Could not approve that.'); }
    mark(txnId, false);
  };

  const change = async (txnId, code, always) => {
    mark(txnId, true);
    try {
      const res = await api.put(`/api/categorization/transactions/${txnId}/category`,
                                { account_code: code, always });
      flash(res.data.learning || 'Category changed.');
      await load();
    } catch (e) { flash(e.response?.data?.detail || 'Could not change that category.'); }
    mark(txnId, false);
  };

  const openRules = async () => {
    setShowRules(true);
    try {
      const res = await api.get('/api/categorization/rules?limit=500');
      setRules(res.data.rules || []);
    } catch (e) { setRules([]); }
  };

  const deactivateRule = async (ruleId) => {
    try {
      await api.patch(`/api/categorization/rules/${ruleId}`, { is_active: false });
      setRules(p => p.filter(r => r.rule_id !== ruleId));
      flash('Rule deactivated. It stays visible in the audit trail and survives the next rebuild.');
    } catch (e) { flash(e.response?.data?.detail || 'Could not deactivate that rule.'); }
  };

  const openHistory = async (txnId) => {
    setHistoryFor(txnId);
    try {
      const res = await api.get(`/api/categorization/transactions/${txnId}/history`);
      setHistory(res.data.decisions || []);
    } catch (e) { setHistory([]); }
  };

  const automaticPct = summary ? Math.round((summary.automatic_share || 0) * 100) : 0;

  return (
    <div className="max-w-[110rem] mx-auto py-8 px-4" data-testid="expense-categorization-page">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Expenses
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {entity} — routine spend is categorized from seven years of Xero history.
            What is left is genuinely new.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runCategorization} disabled={loading}
            data-testid="run-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-900 text-white text-xs font-semibold rounded-lg hover:bg-indigo-950 disabled:opacity-50">
            <Play size={13} /> Categorize new transactions
          </button>
          <button onClick={openRules} data-testid="rules-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50">
            <BookOpen size={13} /> Rules
          </button>
          <button onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg hover:bg-slate-50">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {notice && (
        <div className="mb-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-900"
             data-testid="notice">
          <CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />
          <span>{notice}</span>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <SummaryTile label="Need a category" value={summary.review} tone="amber"
                       sub="genuinely new merchants"
                       active={outcome === 'review'} onClick={() => setOutcome('review')} />
          <SummaryTile label="Suggested" value={summary.suggested} tone="indigo"
                       sub="one click to confirm"
                       active={outcome === 'suggested'} onClick={() => setOutcome('suggested')} />
          <SummaryTile label="Categorized" value={summary.categorized} tone="emerald"
                       sub={`${summary.set_manually} set by hand`}
                       active={outcome === 'auto'} onClick={() => setOutcome('auto')} />
          <SummaryTile label="Not an expense" value={summary.not_an_expense} tone="slate"
                       sub="transfers and card payments"
                       active={outcome === 'blocked'} onClick={() => setOutcome('blocked')} />
          <SummaryTile label="Handled automatically" value={`${automaticPct}%`} tone="slate"
                       sub={`${summary.active_rules} active rules`}
                       active={false} onClick={() => setOutcome('')} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-[16rem]">
          <Search size={14} className="text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search descriptions…" data-testid="search-input"
                 className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 outline-none focus:border-indigo-400" />
        </div>

        <select value={outcome} onChange={e => setOutcome(e.target.value)} data-testid="outcome-filter"
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white">
          {OUTCOMES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>

        <select value={accountCode} onChange={e => setAccountCode(e.target.value)} data-testid="category-filter"
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white max-w-[14rem]">
          <option value="">Any category</option>
          {accounts.map(a => (
            <option key={a.account_id || a.account_code} value={a.account_code}>
              {a.account_code} — {a.account_name}
            </option>
          ))}
        </select>

        <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)} data-testid="account-filter"
                className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white max-w-[14rem]">
          <option value="">Any bank or card</option>
          {banks.map(b => (
            <option key={b.bank_account_id} value={b.bank_account_id}>{b.bank_account_name}</option>
          ))}
        </select>

        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from"
               className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to"
               className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white" />

        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={manualOnly} onChange={e => setManualOnly(e.target.checked)}
                 data-testid="manual-filter" />
          Set by hand only
        </label>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <CheckCircle2 size={36} className="text-emerald-400 mx-auto mb-3" />
          <p className="font-semibold text-slate-700">Nothing here.</p>
          <p className="text-sm text-slate-400 mt-1">
            {outcome === 'review'
              ? 'Every transaction has a category. Nothing needs you.'
              : 'No transactions match those filters.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
                <th className="text-left px-3 py-3 font-semibold w-24">Date</th>
                <th className="text-left px-3 py-3 font-semibold w-40">Bank / card</th>
                <th className="text-left px-3 py-3 font-semibold w-44">Merchant</th>
                <th className="text-left px-3 py-3 font-semibold">Description</th>
                <th className="text-right px-3 py-3 font-semibold w-28">Amount</th>
                <th className="text-left px-3 py-3 font-semibold w-48">Category</th>
                <th className="text-left px-3 py-3 font-semibold w-32">How</th>
                <th className="text-left px-3 py-3 font-semibold w-28">Confidence</th>
                <th className="px-3 py-3 w-56"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(txn => (
                <Row key={txn.txn_id} txn={txn} accounts={accounts} busy={busy[txn.txn_id]}
                     onApprove={approve} onChange={change} onHistory={openHistory} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRules && (
        <div className="fixed inset-0 bg-slate-900/30 flex items-stretch justify-end z-50"
             onClick={() => setShowRules(false)}>
          <div className="bg-white border-l border-slate-200 w-full max-w-3xl overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="rules-panel">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">Categorization rules</h2>
                <p className="text-[11px] text-slate-500">
                  Every rule carries the evidence behind it. A rule with no agreement in the
                  evidence is kept and marked inert — it is the reason a merchant stays in review.
                </p>
              </div>
              <button onClick={() => setShowRules(false)} className="text-slate-400 hover:text-slate-700">
                <X size={16} />
              </button>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Matches</th>
                  <th className="text-left px-4 py-2.5 font-semibold w-44">Category</th>
                  <th className="text-left px-4 py-2.5 font-semibold w-36">Evidence</th>
                  <th className="text-left px-4 py-2.5 font-semibold w-32">Does</th>
                  <th className="px-4 py-2.5 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.rule_id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-800">
                        {r.merchant || `"${r.pattern}" in the description`}
                      </div>
                      {r.descriptor && <div className="text-[10px] text-slate-400">with “{r.descriptor}”</div>}
                      <div className="text-[10px] text-slate-400">{r.tier_label}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-slate-700">{r.account_name || r.account_code}</div>
                      <div className="text-[10px] font-mono text-slate-400">{r.account_code}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 tabular-nums">
                      {r.evidence_total > 0
                        ? `${r.evidence_matching} of ${r.evidence_total}`
                        : 'created directly'}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.authority === 'auto'
                        ? <Pill tone="emerald"><Sparkles size={10} /> Applies</Pill>
                        : r.authority === 'suggested'
                          ? <Pill tone="indigo"><Wand2 size={10} /> Suggests</Pill>
                          : <Pill tone="slate" title={r.authority_note}><Ban size={10} /> Inert</Pill>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => deactivateRule(r.rule_id)}
                              className="text-[11px] text-slate-400 hover:text-red-600">
                        Turn off
                      </button>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                    No rules yet. Run a rebuild from the Xero history, or categorize a few
                    transactions and they will start to appear.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="fixed inset-0 bg-slate-900/30 flex items-center justify-center z-50 p-4"
             onClick={() => setHistoryFor(null)}>
          <div className="bg-white rounded-xl border border-slate-200 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
               onClick={e => e.stopPropagation()} data-testid="history-panel">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-800">Every decision made about this transaction</h2>
              <button onClick={() => setHistoryFor(null)} className="text-slate-400 hover:text-slate-700">
                <X size={16} />
              </button>
            </div>
            <div className="divide-y divide-slate-100">
              {history.length === 0 && (
                <div className="px-5 py-8 text-xs text-slate-400 text-center">No decisions recorded yet.</div>
              )}
              {history.map(h => (
                <div key={h.decision_id} className={`px-5 py-3 ${h.superseded_at ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-800">
                      {h.account_name || h.account_code || 'No category'}
                    </span>
                    {!h.superseded_at && <Pill tone="emerald">In force</Pill>}
                    {h.superseded_at && <Pill tone="slate">Replaced</Pill>}
                    <span className="text-[10px] text-slate-400 ml-auto">{h.decided_at}</span>
                  </div>
                  <div className="text-[11px] text-slate-600">{h.explanation}</div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    {h.tier_label}
                    {h.decided_by && ` · ${h.decided_by}`}
                    {h.previous_account_code && ` · replaced ${h.previous_account_code}`}
                    {h.rule_id && ` · rule ${h.rule_id}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
